import React, { PropsWithChildren, useContext, useEffect, useMemo } from "react";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config";
import { useTheme } from "@mui/material";
import { UnisphereProvider } from "@unisphere/runtime-react";
import { UnisphereWorkspaceConfig } from "@unisphere/runtime";
import { version } from "@kaltura/mediaspace-shared-data-kms-version";
import * as InAppMessaging from "@kaltura/unisphere-in-app-messaging-core";
import { registerElementInGlobalKalturaVersions } from "@unisphere/core";

/*
 * z-index of DOM elements that Unisphere renders on the body tag.
 * They should be on top of all regular elements on the page, and below MUI modals (1300 by default)
 */
const unisphereZIndex = 1000;

/**
 * Render Unisphere provider, initialized with values from KMS configs
 */
export const KmsUnisphereProvider = ({ children }: PropsWithChildren) => {
    const config = useContext(ConfigContext);
    const serverUrl = config.application.unisphereWorkspaceUrl;
    const application = config.application.isKAF ? "KAF" : "KMS";
    const { partnerId, userRole, profileName } = config.application;
    const pageType = config.analytics?.pageType ?? "Unknown";
    const inAppMessagingProps = config.inAppMessaging;

    useEffect(() => {
        registerElementInGlobalKalturaVersions({
            _schemaVersion: "1",
            type: "host",
            productName: "MediaSpace",
            subProductName: profileName,
            version,
            origin: "url",
        });
    }, [profileName]);

    const theme = useTheme();

    const unisphereConfig = useMemo<UnisphereWorkspaceConfig>(() => {
        const runtimes: UnisphereWorkspaceConfig["runtimes"] = [
            {
                widgetName: "unisphere.widget.notifications",
                runtimeName: "notifications",
                settings: {},
                visuals: [
                    {
                        type: "container",
                        target: { target: "body" },
                        settings: {},
                    },
                ],
                ui: {
                    bodyContainer: {
                        zIndex: unisphereZIndex,
                    },
                },
            },
        ];

        if (inAppMessagingProps) {
            runtimes.push({
                runtimeName: InAppMessaging.EngineRuntimeName,
                widgetName: InAppMessaging.WidgetName,
                settings: {
                    hostUser: {
                        roles: [userRole],
                        partnerId: String(partnerId),
                        ks: inAppMessagingProps.ks,
                    },
                    hostApp: {
                        currentPage: pageType,
                    },
                } satisfies InAppMessaging.EngineRuntimeSettings,
            });
        }

        return {
            serverUrl,
            appId: application,
            appProfileId: profileName,
            appVersion: version,
            runtimes,
            ui: {
                bodyContainer: {
                    zIndex: unisphereZIndex,
                },
            },
            /*
             * appProfileId will be added to the UnisphereWorkspaceConfig type only in the next Unisphere release.
             * TODO: remove the "as UnisphereWorkspaceConfig" clause after the release.
             */
        } as UnisphereWorkspaceConfig;
    }, [serverUrl, application, partnerId, userRole, profileName, pageType, inAppMessagingProps]);

    return (
        <UnisphereProvider
            initialConfiguration={unisphereConfig}
            kalturaTheme={theme.kaltura}
            language={config.application.currentLocaleCode ?? "en"}
        >
            {children}
        </UnisphereProvider>
    );
};
